/**
 * Lecture de l'enveloppe complète d'une réponse — `{ data, meta }`.
 *
 * Le SDK officiel ne rend que `data` : c'est le bon choix par défaut, il
 * évite de manipuler l'enveloppe partout. Mais un endpoint transporte une
 * information utile dans `meta` : la **fiche entreprise**.
 *
 * En cas de panne du fournisseur officiel, l'API préfère servir une fiche
 * datée plutôt qu'une erreur, et le signale :
 *
 * ```json
 * {
 *   "data": { "name": "…" },
 *   "meta": { "stale": true, "fetched_at": "2026-07-28T09:12:00+00:00" }
 * }
 * ```
 *
 * `meta.stale` vaut `true` quand la donnée servie n'a pas pu être rafraîchie.
 * À vous de décider : l'ignorer, afficher un bandeau, proposer de réessayer.
 * DocInfoPro affiche un bandeau — c'est plus honnête vis-à-vis du visiteur.
 *
 * Ce module montre par la même occasion à quoi ressemble un appel **en HTTP
 * direct**, sans le SDK : les mêmes en-têtes, la même enveloppe, la même
 * normalisation des erreurs. Tout ce que fait le SDK peut se faire ainsi.
 */

import { DashLiteApiError   } from '@dash-lite/sdk';
import type {Company, DashLiteErrorCode} from '@dash-lite/sdk';
import { appConfig } from '@/config/env';
import { dashlite, instrumentedFetch } from '@/lib/dashlite';

/** Métadonnées de fraîcheur accompagnant une fiche entreprise. */
export type CompanyMeta = {
    /** `true` : fiche servie depuis le cache, le fournisseur ne répondait pas. */
    stale?: boolean;
    /** Date de constitution de la fiche servie. */
    fetched_at?: string;
};

export type CompanyEnvelope = {
    data: Company;
    meta: CompanyMeta;
};

/**
 * `GET /companies/{registration_number}`, enveloppe comprise.
 *
 * Le token et la clé de site sont repris du client SDK : on ne duplique pas
 * l'état de session, on le lit là où il vit déjà.
 */
export async function getCompanyWithMeta(registrationNumber: string): Promise<CompanyEnvelope> {
    const token = dashlite.getToken();

    // `instrumentedFetch` est le même `fetch` que celui confié au SDK : cet
    // appel apparaît donc lui aussi dans l'inspecteur d'API. Dans votre
    // projet, ce serait simplement `fetch`.
    const response = await instrumentedFetch(
        `${appConfig.apiUrl}/api/v1/companies/${encodeURIComponent(registrationNumber)}`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                // Sur TOUS les appels, sans exception : c'est cet en-tête qui
                // identifie le site ET son environnement.
                'X-Site-Key': appConfig.siteKey,
                // Seulement sur les routes authentifiées.
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        },
    );

    const payload = (await response.json().catch(() => null)) as {
        data?: Company;
        meta?: CompanyMeta;
        error?: { code: DashLiteErrorCode; message: string; details?: Record<string, unknown> };
    } | null;

    if (!response.ok) {
        // On relève la même exception que le SDK : les écrans n'ont qu'un seul
        // type d'erreur à connaître, quel que soit le chemin d'appel.
        throw new DashLiteApiError(
            payload?.error?.code ?? 'server_error',
            payload?.error?.message ?? 'Une erreur inattendue est survenue.',
            response.status,
            payload?.error?.details ?? {},
        );
    }

    if (!payload?.data) {
        throw new DashLiteApiError('server_error', 'Réponse inattendue de l’API.', response.status, {});
    }

    return { data: payload.data, meta: payload.meta ?? {} };
}
